import { type ColumnSizingState } from '@tanstack/react-table';
import { type CSSProperties } from 'react';

import { type ResizableTableColumn } from './types.ts';

export const DEFAULT_COLUMN_SIZE = 160;
export const DEFAULT_MIN_COLUMN_SIZE = 50;
export const DEFAULT_MAX_COLUMN_SIZE = 800;
export const CUSTOM_SCROLLBAR_MIN_THUMB_SIZE = 24;
export const SCROLLBAR_OVERFLOW_TOLERANCE = 1;

export type ColumnSizeVars = CSSProperties & Record<`--${string}`, number>;

export type StoredColumnSizing = {
  columnLayoutSignature: string;
  columnSizing: ColumnSizingState;
};

export type ScrollbarThumbMetrics = {
  size: number;
  offset: number;
};

export type ResizableTableScrollMetrics = {
  scrollLeft: number;
  scrollTop: number;
  scrollWidth: number;
  scrollHeight: number;
  clientWidth: number;
  clientHeight: number;
  verticalTrackLength: number;
  horizontalTrackLength: number;
};

export type ColumnSizeConfig = {
  id: string;
  size: number;
  minSize: number;
  maxSize: number;
};

export const DEFAULT_SCROLL_METRICS: ResizableTableScrollMetrics = {
  scrollLeft: 0,
  scrollTop: 0,
  scrollWidth: 0,
  scrollHeight: 0,
  clientWidth: 0,
  clientHeight: 0,
  verticalTrackLength: 0,
  horizontalTrackLength: 0,
};

export const getColumnMinSize = <TData>(column: ResizableTableColumn<TData>) =>
  Math.max(column.minSize ?? DEFAULT_MIN_COLUMN_SIZE, DEFAULT_MIN_COLUMN_SIZE);

export const getColumnMaxSize = <TData>(column: ResizableTableColumn<TData>, minSize: number) =>
  Math.max(column.maxSize ?? DEFAULT_MAX_COLUMN_SIZE, minSize);

export const clampColumnSize = (size: number, minSize: number, maxSize: number) =>
  Math.min(Math.max(size, minSize), maxSize);

export const clampValue = (value: number, minValue: number, maxValue: number) =>
  Math.min(Math.max(value, minValue), maxValue);

export const getColumnBaseSize = <TData>(column: ResizableTableColumn<TData>) => {
  const minSize = getColumnMinSize(column);
  const maxSize = getColumnMaxSize(column, minSize);

  return clampColumnSize(column.size ?? DEFAULT_COLUMN_SIZE, minSize, maxSize);
};

export const getColumnSizeConfig = <TData>(
  column: ResizableTableColumn<TData>
): ColumnSizeConfig => {
  const minSize = getColumnMinSize(column);
  const maxSize = getColumnMaxSize(column, minSize);

  return {
    id: column.id,
    size: getColumnBaseSize(column),
    minSize,
    maxSize,
  };
};

export const getColumnCurrentSize = <TData>(
  column: ResizableTableColumn<TData>,
  columnSizing: ColumnSizingState
) => {
  const minSize = getColumnMinSize(column);
  const maxSize = getColumnMaxSize(column, minSize);

  return clampColumnSize(columnSizing[column.id] ?? getColumnBaseSize(column), minSize, maxSize);
};

export const getColumnTotalSize = <TData>(
  columns: ResizableTableColumn<TData>[],
  columnSizing: ColumnSizingState
) => columns.reduce((acc, column) => acc + getColumnCurrentSize(column, columnSizing), 0);

export const getColumnLayoutSignature = <TData>(columns: ResizableTableColumn<TData>[]) =>
  columns
    .map(
      ({ enableResizing, id, maxSize, minSize, size }) =>
        `${id}:${size ?? ''}:${minSize ?? ''}:${maxSize ?? ''}:${enableResizing ?? ''}`
    )
    .join('|');

export const areColumnSizingEqual = (a: ColumnSizingState, b: ColumnSizingState) => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  return (
    aKeys.length === bKeys.length &&
    aKeys.every((key) => Math.abs((a[key] ?? 0) - (b[key] ?? 0)) < 0.5)
  );
};

/**
 * Creates an isolated localStorage key for a table layout on a route.
 */
export const getStorageKey = (pathname: string, tableStorageId?: string) => {
  const routeKey = pathname || '/';

  return tableStorageId ? `${routeKey}:resizable-table:${tableStorageId}` : routeKey;
};

export const getStoredColumnSizing = (
  storageKey: string,
  columnLayoutSignature: string
): ColumnSizingState | null => {
  try {
    const rawValue = window.localStorage.getItem(storageKey);

    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue) as StoredColumnSizing;

    if (
      !parsedValue ||
      parsedValue.columnLayoutSignature !== columnLayoutSignature ||
      typeof parsedValue.columnSizing !== 'object' ||
      parsedValue.columnSizing === null
    ) {
      window.localStorage.removeItem(storageKey);
      return null;
    }

    return Object.entries(parsedValue.columnSizing).reduce<ColumnSizingState>(
      (acc, [key, value]) => {
        if (typeof value === 'number' && Number.isFinite(value)) {
          acc[key] = value;
        }

        return acc;
      },
      {}
    );
  } catch {
    window.localStorage.removeItem(storageKey);
    return null;
  }
};

export const setStoredColumnSizing = (
  storageKey: string,
  columnLayoutSignature: string,
  columnSizing: ColumnSizingState
) => {
  const payload: StoredColumnSizing = {
    columnLayoutSignature,
    columnSizing,
  };

  window.localStorage.setItem(storageKey, JSON.stringify(payload));
};

export const distributeRemainingWidthProportionally = (
  columnSizing: ColumnSizingState,
  columns: ColumnSizeConfig[],
  remainingWidth: number
) => {
  let widthToDistribute = remainingWidth;
  let growableColumns = columns.filter(
    (column) => (columnSizing[column.id] ?? column.size) < column.maxSize
  );

  while (widthToDistribute > 0.5 && growableColumns.length > 0) {
    const totalWeight = growableColumns.reduce(
      (acc, column) => acc + (columnSizing[column.id] ?? column.size),
      0
    );
    let distributedWidth = 0;

    growableColumns.forEach((column) => {
      const currentSize = columnSizing[column.id] ?? column.size;
      const weight = totalWeight > 0 ? currentSize / totalWeight : 1 / growableColumns.length;
      const widthToAdd = Math.min(widthToDistribute * weight, column.maxSize - currentSize);

      if (widthToAdd <= 0) {
        return;
      }

      columnSizing[column.id] = currentSize + widthToAdd;
      distributedWidth += widthToAdd;
    });

    if (distributedWidth <= 0) {
      break;
    }

    widthToDistribute -= distributedWidth;
    growableColumns = growableColumns.filter(
      (column) => (columnSizing[column.id] ?? column.size) < column.maxSize
    );
  }
};

export const normalizeColumnSizing = <TData>(
  columns: ResizableTableColumn<TData>[],
  columnSizing: ColumnSizingState
): ColumnSizingState => {
  const columnConfigs = columns.map(getColumnSizeConfig);

  return columnConfigs.reduce<ColumnSizingState>((acc, column) => {
    acc[column.id] = clampColumnSize(
      columnSizing[column.id] ?? column.size,
      column.minSize,
      column.maxSize
    );

    return acc;
  }, {});
};

export const getFilledColumnSizing = <TData>(
  columns: ResizableTableColumn<TData>[],
  containerWidth: number
): ColumnSizingState => {
  const baseColumnSizing = normalizeColumnSizing(columns, {});

  if (containerWidth <= 0) {
    return baseColumnSizing;
  }

  const columnConfigs = columns
    .filter((column) => column.enableResizing !== false)
    .map(getColumnSizeConfig);
  const totalWidth = getColumnTotalSize(columns, baseColumnSizing);

  if (totalWidth >= containerWidth || columnConfigs.length === 0) {
    return baseColumnSizing;
  }

  const nextColumnSizing = { ...baseColumnSizing };
  distributeRemainingWidthProportionally(
    nextColumnSizing,
    columnConfigs,
    containerWidth - totalWidth
  );

  return normalizeColumnSizing(columns, nextColumnSizing);
};

export const getColumnSizeVars = <TData>(
  columns: ResizableTableColumn<TData>[],
  columnSizing: ColumnSizingState
): ColumnSizeVars => {
  const columnSizeVars: Record<`--${string}`, number> = {};

  columns.forEach((column) => {
    const columnSize = getColumnCurrentSize(column, columnSizing);

    columnSizeVars[`--header-${column.id}-size` as `--${string}`] = columnSize;
    columnSizeVars[`--col-${column.id}-size` as `--${string}`] = columnSize;
  });

  return columnSizeVars as ColumnSizeVars;
};

export const hasScrollableOverflow = (contentSize: number, viewportSize: number) =>
  contentSize - viewportSize > SCROLLBAR_OVERFLOW_TOLERANCE;

export const getScrollableDistance = (contentSize: number, viewportSize: number) =>
  Math.max(contentSize - viewportSize, 0);

export const getScrollbarThumbMetrics = ({
  contentSize,
  scrollPosition,
  trackSize,
  viewportSize,
}: {
  contentSize: number;
  scrollPosition: number;
  trackSize: number;
  viewportSize: number;
}): ScrollbarThumbMetrics => {
  if (trackSize <= 0 || viewportSize <= 0 || !hasScrollableOverflow(contentSize, viewportSize)) {
    return {
      size: 0,
      offset: 0,
    };
  }

  const rawSize = (viewportSize / contentSize) * trackSize;
  const size = Math.min(Math.max(rawSize, CUSTOM_SCROLLBAR_MIN_THUMB_SIZE), trackSize);
  const maxOffset = Math.max(trackSize - size, 0);
  const maxScrollPosition = getScrollableDistance(contentSize, viewportSize);
  const offset = maxScrollPosition > 0 ? (scrollPosition / maxScrollPosition) * maxOffset : 0;

  return {
    size,
    offset: clampValue(offset, 0, maxOffset),
  };
};

export const getScrollPositionFromTrackPointer = ({
  contentSize,
  pointerOffset,
  thumbSize,
  trackSize,
  viewportSize,
}: {
  contentSize: number;
  pointerOffset: number;
  thumbSize: number;
  trackSize: number;
  viewportSize: number;
}) => {
  const maxThumbOffset = Math.max(trackSize - thumbSize, 0);
  const maxScrollPosition = getScrollableDistance(contentSize, viewportSize);

  if (maxThumbOffset <= 0 || maxScrollPosition <= 0) {
    return 0;
  }

  const thumbOffset = clampValue(pointerOffset - thumbSize / 2, 0, maxThumbOffset);

  return (thumbOffset / maxThumbOffset) * maxScrollPosition;
};
